import axios, { AxiosResponse } from 'axios';

import { apiWrapper, axiosAgentApi, axiosApi, ResultDatas } from 'utils/api';
import {
  EcgAnnotationModel,
  ECGEventModel,
  EcgEventTypeModel,
  PatientModel,
  PatientStudyModel,
} from 'core/models';
import { Patient, RawPatient } from 'core/models/patientModel';
import { PatientFormData } from 'pages/patientsManagement/components/patientForm/PatientForm';
import { Pagination } from 'utils/table';
import {
  Device,
  SensorEnum,
  PatientDevices,
  RawDevice,
} from 'core/models/deviceModel';
import { ECGEvent, RawECGEvent } from 'core/models/eventECGModel';
import {
  PatientStudy,
  PatientStudyDayModel,
  RawPatientStudy,
} from 'core/models/patientStudyModel';
import { ECGChartData } from 'pages/patientsManagement/components/ecgChart/ECGChart';
import { EcgEventType, RawEcgEventType } from 'core/models/eventECGTypeModel';
import {
  EcgAnnotation,
  EcgAnnotationFormData,
  RawEcgAnnotation,
} from 'core/models/ecgAnnotationModel';
import { toUtcTimestamp } from 'utils/time';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import changeData from 'utils/ecgUtil';
import { handlePatientTime, handleTimeZoneTimeToUTC } from 'utils/dayjsFormat';
import { EventType } from 'core/models/eventTypeModel';
import { PatientBase } from 'core/models/patientModelBase';
import { camelToSnake } from 'utils/util';
import { axiosDefaultConfig } from 'core/config';

dayjs.extend(utc);
export const apiPatientsAllList = async (
  queryValue?: string,
  isLoading?: boolean,
): Promise<AxiosResponse<Pagination<PatientBase>> | void> => {
  const success = async (): Promise<AxiosResponse<
    Pagination<PatientBase>
  > | void> => {
    const response = await axiosApi().get<Pagination<PatientBase>>(
      `/patients/all`,
      {
        params: { queryValue },
      },
    );
    return {
      ...response,
      data: {
        ...response.data,
        data: response?.data?.data || [],
      },
    };
  };

  return apiWrapper<Pagination<PatientBase>>({
    success,
    isLoading,
  });
};

export const patientsList = async (
  page?: number,
  pageSize?: number,
  s?: string,
  type?: string,
  studiesId?: string,
  siteId?: string,
  queryParam?: any,
  isManage?: boolean,
  needOnlineStatus?: boolean,
  sortField?: string,
  sortOrder?: string,
): Promise<AxiosResponse<Pagination<Patient>> | void> => {
  const success = async (): Promise<AxiosResponse<
    Pagination<Patient>
  > | void> => {
    const params: any = {
      studiesId,
      siteId,
      type,
      pageSize,
      page,
      ...(queryParam || {}),
      queryValue: queryParam?.queryValue || s,
      isManage,
      needOnlineStatus,
    };
    if (sortField) {
      params.order = camelToSnake(sortField);
      params.desc = sortOrder === '-1';
    }
    const response = await axiosApi().get<Pagination<RawPatient>>(`/patients`, {
      params,
    });
    return {
      ...response,
      data: {
        ...response.data,
        data:
          response?.data?.data?.map((patient) => new PatientModel(patient)) ||
          [],
      },
    };
  };

  return apiWrapper<Pagination<Patient>>({
    success,
  });
};

export const sPatientsList = async (
  $skip?: number,
  $limit?: number,
  s?: string,
  type?: string,
  studiesId?: string,
): Promise<AxiosResponse<Pagination<any>> | void> => {
  const success = async (): Promise<AxiosResponse<Pagination<any>> | void> => {
    const response = await axiosApi().get<Pagination<any>>(`/patients`, {
      params: { studiesId, type, s, $skip, $limit },
    });
    return {
      ...response,
      data: {
        ...response.data,
        data: response.data.data,
      },
    };
  };

  return apiWrapper<Pagination<any>>({
    success,
  });
};

export interface PatientDashboardItem {
  id: string | number;
  studyDate: string;
  events: Partial<ECGEvent>[];
}

export const patientStudiesListNew = async (
  patient: string,
  $skip?: number,
  $limit?: number,
  sortField?: string,
  sortOrder?: string,
  type?: string,
): Promise<AxiosResponse<Pagination<PatientStudyDayModel>> | void> => {
  const success = async (): Promise<AxiosResponse<
    Pagination<PatientStudyDayModel>
  > | void> => {
    const _s = sortField
      ? `?order=${sortField}&desc=${sortOrder === '-1'}`
      : '';
    const response = await axiosApi().get<Pagination<PatientStudyDayModel>>(
      `/patientDay${_s}`,
      {
        params: { patientId: patient, page: $skip, pageSize: $limit, type },
      },
    );
    const data_ = response.data.data;
    data_?.forEach((item) => {
      if (item != null && item?.timeZoneInfo != null) {
        if (item.timeZoneInfo.isDst) {
          item.timeZoneInfo.timeZoneDescription1 = `${item.timeZoneInfo.timeZoneOffsetStr}/${item.timeZoneInfo.dstStr}`;
        } else {
          item.timeZoneInfo.timeZoneDescription1 = `${item.timeZoneInfo.timeZoneOffsetStr}/${item.timeZoneInfo.estStr}`;
        }
        item.timeZoneInfo.timeZoneDescription1 = `(${item.timeZoneInfo.timeZoneDescription1})`;
        item.timeZoneInfo.timeZoneDescription2 = `(${item.timeZoneInfo.timeZoneOffsetStr})`;
      }
    });
    response.data.data = data_;
    return {
      ...response,
      data: {
        ...response.data,
        data: response?.data?.data || [],
      },
    };
  };

  return apiWrapper<Pagination<PatientStudyDayModel>>({
    success,
  });
};

export const ecgAutoReport = async (
  patientId: string,
  startTime?: string,
  endTime?: string,
): Promise<AxiosResponse<any> | void> => {
  const success = async (): Promise<AxiosResponse<any> | void> => {
    const response = await axiosApi().get<any>(`/ecgAutoReport/${patientId}`, {
      params: { startTime, endTime },
    });
    return {
      ...response,
      data: response.data,
    };
  };

  return apiWrapper<any>({
    success,
  });
};

export const patientStudyGet = async (
  studyId: string,
  type?: string,
): Promise<AxiosResponse<PatientStudy> | void> => {
  const success = async (): Promise<AxiosResponse<PatientStudy> | void> => {
    const response = await axiosApi().get<RawPatientStudy>(
      `/patientStudies/${studyId}`,
      {
        params: {
          type,
        },
      },
    );
    return {
      ...response,
      data: new PatientStudyModel(response.data),
    };
  };

  return apiWrapper<PatientStudy>({
    success,
  });
};

export const patientStudyGetBystartAndEndPatientStudiesId = async (
  patientId: string,
  startPatientStudiesId: string,
  endPatientStudiesId: string,
  startDay?: string,
  endDay?: string,
): Promise<AxiosResponse<PatientStudy> | void> => {
  const success = async (): Promise<AxiosResponse<PatientStudy> | void> => {
    const response = await axiosApi().get<RawPatientStudy>(
      `/charData/mv/xInfo`,
      {
        params: {
          patientId,
          startPatientStudiesId,
          endPatientStudiesId,
          startDay,
          endDay,
        },
      },
    );
    return {
      ...response,
      data: new PatientStudyModel(response.data),
    };
  };

  return apiWrapper<PatientStudy>({
    success,
  });
};

export const patientStudyEventsList = async (
  patientDay: string,
  processId: string | undefined,
  $skip?: number,
  $limit?: number,
  patientId?: string,
  validateStatus?: any,
): Promise<AxiosResponse<Pagination<ECGEvent>> | void> => {
  const success = async (): Promise<AxiosResponse<
    Pagination<ECGEvent>
  > | void> => {
    const response = await axiosApi().get<Pagination<RawECGEvent>>(
      `/ecgEvents`,
      {
        params: {
          validateStatus,
          patientDay,
          processId,
          page: $skip,
          pageSize: $limit,
          patient: patientId,
        },
      },
    );

    return {
      ...response,
      data: {
        ...response.data,
        data:
          response?.data?.data?.map(
            (ecgEvent) => new ECGEventModel(ecgEvent),
          ) || [],
      },
    };
  };

  return apiWrapper<Pagination<ECGEvent>>({
    success,
  });
};

export const patientStudyEventsListNew = async (
  $patient: string,
  $startTime: string,
  $endTime: string,
): Promise<AxiosResponse<Pagination<ECGEvent>> | void> => {
  const success = async (): Promise<AxiosResponse<
    Pagination<ECGEvent>
  > | void> => {
    const response = await axiosApi().get<Pagination<RawECGEvent>>(
      `/ecgEvents`,
      {
        params: {
          patient: $patient,
          start: toUtcTimestamp($startTime),
          finish: toUtcTimestamp($endTime),
        },
      },
    );

    return {
      ...response,
      data: {
        ...response.data,
        data:
          response?.data?.data?.map(
            (ecgEvent) => new ECGEventModel(ecgEvent),
          ) || [],
      },
    };
  };

  return apiWrapper<Pagination<ECGEvent>>({
    success,
  });
};

export const patientGet = async (
  id: string,
  type?: string,
): Promise<AxiosResponse<any> | void> => {
  const success = async (): Promise<AxiosResponse<any> | void> => {
    let url = `/patients/${id}`;
    if (type != null) {
      url += `?type=${type}&needOnlineStatus=true`;
    } else {
      url += `?needOnlineStatus=true`;
    }
    const response = await axiosApi().get<any>(url);
    return {
      ...response,
      data: response.data,
    };
  };

  return apiWrapper<any>({
    success,
  });
};

export const apiPatientSensorList = async (
  patientId: string,
): Promise<AxiosResponse<any> | void> => {
  const success = async (): Promise<AxiosResponse<any> | void> => {
    const response = await axiosApi().get<any>(`/patient-sensor`, {
      params: { patientId },
    });
    return {
      ...response,
      data: response?.data?.data || [],
    };
  };

  return apiWrapper<any>({
    success,
  });
};

export interface PatientFormDataApi extends Omit<PatientFormData, 'codes'> {
  codes?: string[];
}

interface PatientPost {
  data: PatientFormDataApi;
}

interface PatientPatch extends PatientPost {
  id: string;
}

export const patientPost = async ({
  data,
}: PatientPost): Promise<AxiosResponse<Patient> | void> => {
  const success = async (): Promise<AxiosResponse> => {
    return axiosApi().post<RawPatient>(`/patients`, data);
  };

  return apiWrapper<Patient>({
    success,
  });
};

export const patientPatch = async ({
  id,
  data,
}: PatientPatch): Promise<AxiosResponse<Patient> | void> => {
  const success = async (): Promise<AxiosResponse> => {
    return axiosApi().patch<RawPatient>(`/patients/${id}`, data);
  };

  return apiWrapper<Patient>({
    success,
  });
};

export const rpmPatientPatch = async ({
  id,
  data,
}: PatientPatch): Promise<AxiosResponse<Patient> | void> => {
  const success = async (): Promise<AxiosResponse> => {
    return axiosApi().patch<RawPatient>(`/patients/${id}/patchRpm`, data);
  };

  return apiWrapper<Patient>({
    success,
  });
};

export interface PatienBatchtFormDataApi {
  id: string;
  rpmGroupIds?: string[];
}

interface PatientBatchPatch {
  patients: PatienBatchtFormDataApi[];
}

export const patientBatchPatch = async ({
  patients,
}: PatientBatchPatch): Promise<AxiosResponse<Patient> | void> => {
  const success = async (): Promise<AxiosResponse> => {
    return axiosApi().patch<RawPatient>(`/patients/batch`, { patients });
  };

  return apiWrapper<Patient>({
    success,
  });
};

export interface PairProps {
  patientId: string;
  sensorId: string;
}
export const apiPatientSensorBind = async ({
  patientId,
  sensorId,
}: PairProps): Promise<AxiosResponse<Device> | void> => {
  const success = async (): Promise<AxiosResponse> => {
    return axiosApi().post<RawDevice>(`/patient-sensor`, {
      patientId,
      sensorId,
    });
  };

  return apiWrapper<Device>({
    success,
  });
};

export const apiPatientSensorDelete = async ({
  patientId,
  sensorId,
}: PairProps): Promise<AxiosResponse<Device> | void> => {
  const success = async (): Promise<AxiosResponse> => {
    return axiosApi().delete<RawDevice>(`/patient-sensor`, {
      params: { patientId, sensorId },
    });
  };

  return apiWrapper<Device>({
    success,
  });
};

export const ecgEventDataGet = async (
  ecgEvent: string,
): Promise<AxiosResponse<Pagination<ECGChartData>> | void> => {
  const success = async (): Promise<AxiosResponse<
    Pagination<ECGChartData>
  > | void> => {
    // const response = await axiosApi().get<Pagination<ECGChartData>>(
    //   `/chartData`,
    //   {
    //     params: {
    //       type: SensorEnum.ECG,
    //       eventId: ecgEvent,
    //     },
    //   },
    // );
    const response = await axiosApi().get<any>(`/chartData/ecg`, {
      params: {
        eventId: ecgEvent,
      },
    });
    const data_ = response.data.data;
    if (data_ != null && data_.length > 0) {
      const newData = changeData(data_);
      response.data.data = newData;
    } else {
      response.data.data = response.data.oldData || [];
    }

    return {
      ...response,
      data: response.data as Pagination<ECGChartData>,
    };
  };

  return apiWrapper<Pagination<ECGChartData>>({
    success,
  });
};

export const ecgEventDataGet2 = async (
  ecgEvent: number | string,
  start: number | string,
  finish: number | string,
  fi?: number | string,
  wt?: number | string,
  ft?: number | string,
): Promise<AxiosResponse<ECGChartData[]> | void> => {
  const success = async (): Promise<AxiosResponse<ECGChartData[]> | void> => {
    const response = await axiosApi().get<any>(`/queryData`, {
      params: {
        ecgEvent,
        start,
        finish,
        fi,
        wt,
        ft,
      },
    });
    if (response.data instanceof Array) {
      return response;
    }
    const data_ = response.data.data;
    if (data_ != null && data_.length > 0) {
      const newData = changeData(data_);
      response.data = newData;
      return response;
    }
    return response.data;
  };

  return apiWrapper<ECGChartData[]>({
    success,
  });
};

export const ecgEventDataGetLatest = async (
  patientId: string,
): Promise<AxiosResponse<ECGChartData[]> | void> => {
  const success = async (): Promise<AxiosResponse<ECGChartData[]> | void> => {
    const response = await axiosApi().get<any>(`/chartData/ecg/latest`, {
      params: {
        patientId,
      },
    });
    if (response.data instanceof Array) {
      return response;
    }
    const data_ = response.data.data;
    if (data_ != null && data_.length > 0) {
      const newData = changeData(data_);
      response.data = newData;
      return response;
    }
    return response;
  };

  return apiWrapper<ECGChartData[]>({
    success,
  });
};

export const getEcgAnnotationsList = async (
  ecgEvent: string,
  $skip?: number,
  $limit?: number,
): Promise<AxiosResponse<Pagination<EcgAnnotation>> | void> => {
  const success = async (): Promise<AxiosResponse<
    Pagination<EcgAnnotation>
  > | void> => {
    const response = await axiosApi().get<Pagination<RawEcgAnnotation>>(
      `/annotation`,
      {
        params: {
          type: 'ecg',
          ecgEvent,
          page: $skip,
          pageSize: $limit,
        },
      },
    );

    return {
      ...response,
      data: {
        ...response.data,
        data:
          response?.data?.data?.map(
            (ecgAnnotation) => new EcgAnnotationModel(ecgAnnotation),
          ) || [],
      },
    };
  };

  return apiWrapper<Pagination<EcgAnnotation>>({
    success,
  });
};

export const ecgAnnotationGet = async (
  id: string,
): Promise<AxiosResponse<EcgAnnotation> | void> => {
  const success = async (): Promise<AxiosResponse<EcgAnnotation> | void> => {
    const response = await axiosApi().get<RawEcgAnnotation>(
      `/annotation/${id}?type=ecg`,
    );
    return {
      ...response,
      data: new EcgAnnotationModel(response.data),
    };
  };

  return apiWrapper<EcgAnnotation>({
    success,
  });
};

export const ecgAnnotationGetByEventIds = async (
  eventIds: string[],
  skip?: number,
  limit?: number,
): Promise<AxiosResponse<Pagination<EcgAnnotation>> | void> => {
  const success = async (): Promise<AxiosResponse<
    Pagination<EcgAnnotation>
  > | void> => {
    const response = await axiosApi().post<Pagination<RawEcgAnnotation>>(
      `/annotation/byEventIds`,
      { page: skip, pageSize: limit, ecgEventIds: eventIds },
    );
    const data_ = response.data;
    return {
      ...response,
      data: {
        ...data_,
        data: data_.data.map((d) => {
          return new EcgAnnotationModel(d);
        }),
      },
    };
  };

  return apiWrapper<Pagination<EcgAnnotation>>({
    success,
  });
};

interface ECGAnnotationPostProps {
  data: Omit<EcgAnnotation, 'id' | 'creator' | 'status'>;
  patientTimeZoneName?: string;
}

export const ecgAnnotationPost = async ({
  data,
  patientTimeZoneName,
}: ECGAnnotationPostProps): Promise<AxiosResponse<EcgAnnotation> | void> => {
  const success = async (): Promise<AxiosResponse<EcgAnnotation> | void> => {
    const dataNew = { ...data } as any;
    if (data.start != null) {
      const start = dayjs(data.start).format('YYYY-MM-DD HH:mm:ss.SSS');
      const start_ = handlePatientTime(
        patientTimeZoneName || '',
        start,
      ).valueOf();
      const finish = dayjs(data.finish).format('YYYY-MM-DD HH:mm:ss.SSS');
      const finish_ = handlePatientTime(
        patientTimeZoneName || '',
        finish,
      ).valueOf();

      delete dataNew.start;
      delete dataNew.finish;
      dataNew.startTime = start_;
      dataNew.endTime = finish_;
    }

    if (data.latestECGStart != null) {
      const latestECGStart = dayjs(data.latestECGStart).format(
        'YYYY-MM-DD HH:mm:ss.SSS',
      );
      const latestECGStart_ = handlePatientTime(
        patientTimeZoneName || '',
        latestECGStart,
      ).valueOf();
      const latestECGFinish = dayjs(data.latestECGFinish).format(
        'YYYY-MM-DD HH:mm:ss.SSS',
      );
      const latestECGFinish_ = handlePatientTime(
        patientTimeZoneName || '',
        latestECGFinish,
      ).valueOf();

      delete dataNew.latestECGStart;
      delete dataNew.latestECGFinish;
      dataNew.latestECGStart = latestECGStart_;
      dataNew.latestECGFinish = latestECGFinish_;
    }

    return axiosApi().post<EcgAnnotation>(`/annotation`, {
      ...dataNew,
      type: 'ecg',
    });
  };

  return apiWrapper<EcgAnnotation>({
    success,
  });
};

export const ecgAnnotationPostNew = async (
  data: EcgAnnotationFormData,
): Promise<AxiosResponse<EcgAnnotation> | void> => {
  const success = async (): Promise<AxiosResponse<EcgAnnotation> | void> => {
    return axiosApi().post<EcgAnnotation>(`/annotation`, {
      ...data,
      type: 'ecg',
    });
  };

  return apiWrapper<EcgAnnotation>({
    success,
  });
};

interface ECGAnnotationPostPatch {
  id: string;
  data: any;
}

export const ecgAnnotationPatch = async ({
  id,
  data,
}: ECGAnnotationPostPatch): Promise<AxiosResponse<EcgAnnotation> | void> => {
  const success = async (): Promise<AxiosResponse<EcgAnnotation> | void> => {
    return axiosApi().patch<EcgAnnotation>(`/annotation`, {
      ...data,
      type: 'ecg',
      id,
    });
  };

  return apiWrapper<EcgAnnotation>({
    success,
  });
};

export const ecgAnnotationDelete = async (
  id: string,
): Promise<AxiosResponse<EcgAnnotation> | void> => {
  const success = async (): Promise<AxiosResponse<EcgAnnotation> | void> => {
    return axiosApi().post<EcgAnnotation>(`/annotation/delete`, { ids: [id] });
  };

  return apiWrapper<EcgAnnotation>({
    success,
  });
};

export const ecgEventTypesList = async (
  type: string,
  $skip?: number,
  $limit?: number,
  monitoringProtocolId?: string,
): Promise<AxiosResponse<any> | void> => {
  const success = async (): Promise<AxiosResponse<any> | void> => {
    const response = await axiosApi().get<any>(`/ecgEventTypes`, {
      params: {
        type,
        page: $skip,
        pageSize: $limit,
        monitoringProtocol: monitoringProtocolId,
      },
    });

    return response;
  };

  return apiWrapper<any>({
    success,
  });
};

export const ecgEventGet = async (
  id: string,
): Promise<AxiosResponse<ECGEvent> | void> => {
  const success = async (): Promise<AxiosResponse<ECGEvent> | void> => {
    const response = await axiosApi().get<RawECGEvent>(`/ecgEvents/${id}`);
    return {
      ...response,
      data: new ECGEventModel(response.data),
    };
  };

  return apiWrapper<ECGEvent>({
    success,
  });
};

export const eventTypeList = async (
  id: string,
): Promise<AxiosResponse<any> | void> => {
  const success = async (): Promise<AxiosResponse<any> | void> => {
    const response = await axiosApi().get<any>(
      `/ecgEventTypes?type=eventList&monitoringProtocol=${id}`,
    );
    return {
      ...response,
      data: response.data,
    };
  };

  return apiWrapper<any>({
    success,
  });
};

export const getEditModeEventTypeList = async (): Promise<AxiosResponse<
  EventType[]
> | void> => {
  const success = async (): Promise<AxiosResponse<EventType[]> | void> => {
    const response = await axiosApi().get<{ list: EventType[] }>(
      `/ecgEventTypes/pageView`,
    );
    return {
      ...response,
      data: response?.data?.list || [],
    };
  };

  return apiWrapper<EventType[]>({
    success,
  });
};

interface ECGEventPatchData {
  type?: string;
  flag?: boolean;
  mark?: boolean;
  readStatus?: boolean;
}

export const ecgEventPatch = async (
  id: string,
  data: ECGEventPatchData,
): Promise<AxiosResponse<ECGEvent> | void> => {
  const success = async (): Promise<AxiosResponse<ECGEvent> | void> => {
    return axiosApi().patch<ECGEvent>('/ecgEvents', { ...data, id });
  };

  return apiWrapper<ECGEvent>({
    success,
  });
};

export const ecgEventEdit = async (
  data: any,
): Promise<AxiosResponse<ECGEvent> | void> => {
  const success = async (): Promise<AxiosResponse<ECGEvent> | void> => {
    return axiosApi().patch<ECGEvent>(`/ecgEvents/edit`, data);
  };

  return apiWrapper<ECGEvent>({
    success,
  });
};

export const ecgEventDelete = async (
  id: string,
): Promise<AxiosResponse<ECGEvent> | void> => {
  const success = async (): Promise<AxiosResponse<ECGEvent> | void> => {
    return axiosApi().delete(`/ecgEvents/${id}`);
  };

  return apiWrapper<ECGEvent>({
    success,
  });
};

export const ecgEventDeleteBatch = async (
  data: any,
): Promise<AxiosResponse<ECGEvent> | void> => {
  const success = async (): Promise<AxiosResponse<ECGEvent> | void> => {
    return axiosApi().post(`/ecgEvents/pageViewDelete`, data);
  };

  return apiWrapper<ECGEvent>({
    success,
  });
};

export const clearNote = async (
  sessionId: string,
): Promise<AxiosResponse<PatientDevices> | void> => {
  const success = async (): Promise<AxiosResponse<PatientDevices> | void> => {
    const data = { sessionId };
    return axiosApi().patch(`/patientDevices/clearNote`, data);
  };

  return apiWrapper<PatientDevices>({
    success,
  });
};

export const patientStudyDayGet = async (
  patientId: string,
  type?: string,
): Promise<AxiosResponse<ResultDatas<PatientStudyDayModel>> | void> => {
  const success = async (): Promise<AxiosResponse<
    ResultDatas<PatientStudyDayModel>
  > | void> => {
    const response = await axiosApi().get<ResultDatas<PatientStudyDayModel>>(
      `/patientDay/day`,
      {
        params: {
          patientId,
          type,
        },
      },
    );
    const data_ = response.data.data;
    data_?.forEach((item) => {
      if (item != null && item?.timeZoneInfo != null) {
        item.timeZoneInfo.timeZoneDescription1 =
          item.timeZoneInfo.timeZoneOffsetStr;
        if (item.timeZoneInfo.isDst) {
          item.timeZoneInfo.timeZoneDescription1 = `${item.timeZoneInfo.timeZoneOffsetStr}/${item.timeZoneInfo.dstStr}`;
          item.timeZoneInfo.timeZoneDescription3 = `${item.timeZoneInfo.timeZoneOffsetStr}/${item.timeZoneInfo.dstStr}`;
        } else {
          item.timeZoneInfo.timeZoneDescription1 = `${item.timeZoneInfo.timeZoneOffsetStr}/${item.timeZoneInfo.estStr}`;
          item.timeZoneInfo.timeZoneDescription3 = `${item.timeZoneInfo.timeZoneOffsetStr}/${item.timeZoneInfo.estStr}`;
        }
        item.timeZoneInfo.timeZoneDescription1 = `(${item.timeZoneInfo.timeZoneDescription1})`;
        item.timeZoneInfo.timeZoneDescription2 = `(${item.timeZoneInfo.timeZoneOffsetStr})`;
        item.timeZoneInfo.timeZoneDescription3 = `${item.timeZoneInfo.timeZoneDescription3}`;
      }
    });
    response.data.data = data_;
    return {
      ...response,
      data: response.data,
    };
  };

  return apiWrapper<ResultDatas<PatientStudyDayModel>>({
    success,
  });
};

export const exportPatientTemplate = async (): Promise<void> => {
  const response = await axiosApi().get<any>('/export/patient/template', {
    responseType: 'blob',
  });

  // 尝试从响应头中提取文件名
  const contentDisposition = response.headers['content-disposition'];
  let fileName = 'patient.xlsx'; // 默认文件名

  if (contentDisposition) {
    const fileNameMatch = contentDisposition.match(
      /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/,
    );
    if (fileNameMatch != null && fileNameMatch[1]) {
      fileName = fileNameMatch[1].replace(/['"]/g, ''); // 去除引号
    }
  }

  // 创建下载链接
  const url = window.URL.createObjectURL(new Blob([response.data]));
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', fileName); // 使用提取到的文件名
  document.body.appendChild(link);
  link.click();
  link.remove();

  return undefined;
};

export const apiStartSession = async (
  patientId: string,
): Promise<AxiosResponse<any> | void> => {
  const success = async (): Promise<AxiosResponse<any> | void> => {
    const data = { patientId };
    return axiosApi().post(`/patient-session/start`, data);
  };

  return apiWrapper<any>({
    success,
  });
};

export const apiEndSession = async (
  patientId: string,
): Promise<AxiosResponse<any> | void> => {
  const success = async (): Promise<AxiosResponse<any> | void> => {
    const data = { patientId };
    return axiosApi().patch(`/patient-session/end`, data);
  };

  return apiWrapper<any>({
    success,
  });
};

export const apiPatientSessionAll = async (
  patientId: string,
): Promise<AxiosResponse<any> | void> => {
  const success = async (): Promise<AxiosResponse<any> | void> => {
    const response = await axiosApi().get<any>(
      `/patient-session/all?patientId=${patientId}`,
    );
    return {
      ...response,
      data: response?.data?.data || [],
    };
  };

  return apiWrapper<any[]>({
    success,
  });
};

interface PlanProps {
  id: string;
  sessionPlanStartedAt: string;
  sessionPlanEndedAt: string;
}

export const apiPatientSessionPlan = async (
  data: PlanProps,
): Promise<AxiosResponse<any> | void> => {
  const success = async (): Promise<AxiosResponse<any> | void> => {
    const response = await axiosApi().patch<any>(
      `/patients/${data.id}/session-plan`,
      data,
    );
    return response;
  };

  return apiWrapper<any[]>({
    success,
  });
};

interface BulkPlanProps {
  items: PlanProps[];
}

export const apiPatientSessionPlanBulk = async (
  data: BulkPlanProps,
): Promise<AxiosResponse<any> | void> => {
  const success = async (): Promise<AxiosResponse<any> | void> => {
    const response = await axiosApi().patch<any>(
      `/patients/batch-session-plan`,
      data,
    );
    return response;
  };

  return apiWrapper<any[]>({
    success,
  });
};

export const apiPatientSummary = async (
  id: string,
): Promise<AxiosResponse<any> | void> => {
  const success = async (): Promise<AxiosResponse<any> | void> => {
    const response = await axiosAgentApi().get<any>(
      `/api/patient/summary/${id}`,
    );
    return response;
  };

  return apiWrapper<any[]>({
    success,
  });
};

export const apiPatientSummaryCreate = async (
  id: string,
): Promise<AxiosResponse<any> | void> => {
  const success = async (): Promise<AxiosResponse<any> | void> => {
    const response = await axiosAgentApi().post<any>(
      `/api/patient/render-summary/${id}`,
    );
    return response;
  };

  return apiWrapper<any[]>({
    success,
  });
};

export const apiPatientDashboardSummaryCreate = async (
  id: string,
): Promise<AxiosResponse<any> | void> => {
  const success = async (): Promise<AxiosResponse<any> | void> => {
    const response = await axiosAgentApi().post<any>(
      `/api/patient/render-dashboard-summary/${id}`,
    );
    return response;
  };

  return apiWrapper<any[]>({
    success,
  });
};

export const apiPatientArchive = async (
  id: string,
  pathDir: string,
): Promise<AxiosResponse<any> | void> => {
  const success = async (): Promise<AxiosResponse<any> | void> => {
    const response = await axiosApi().get<any>(`/patient-ecg-dir`, {
      params: {
        patientId: id,
        pathDir,
      },
    });
    return response;
  };

  return apiWrapper<any[]>({
    success,
  });
};

export const apiECGValidateSourceDownlaod = async (
  id: string,
): Promise<any> => {
  const success = async (): Promise<AxiosResponse<any>> => {
    const response = await axiosApi().get<any>('/ecg/telemetry/validate', {
      responseType: 'blob',
      params: {
        id,
      },
    });

    // 尝试从响应头中提取文件名
    const contentDisposition = response.headers['content-disposition'];
    let fileName = 'patient.xlsx'; // 默认文件名

    if (contentDisposition) {
      const fileNameMatch = contentDisposition.match(
        /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/,
      );
      if (fileNameMatch != null && fileNameMatch[1]) {
        fileName = fileNameMatch[1].replace(/['"]/g, ''); // 去除引号
      }
    }

    // 创建下载链接
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', fileName); // 使用提取到的文件名
    document.body.appendChild(link);
    link.click();
    link.remove();
    return response;
  };
  return apiWrapper<any>({
    success,
  });
};

export const apiECGValidateResult = async (
  id: string,
): Promise<AxiosResponse<any> | void> => {
  const response = await axiosApi().get<any>(`/ecgEvent/validate`, {
    params: {
      id,
    },
  });
  return response;
};

interface IPSGetProps {
  patientIds?: string[];
  viewFlag?: boolean;
  name?: string;
  page?: number;
  pageSize?: number;
  order?: string;
  desc?: boolean;
}

export const apiPatientScheduleGet = async (
  params: IPSGetProps,
): Promise<AxiosResponse<any> | void> => {
  const success = async (): Promise<AxiosResponse<any>> => {
    const response = await axiosApi().get<any>(`/schedulePatient`, {
      params,
    });
    return response;
  };
  return apiWrapper<any>({
    success,
  });
};

export const apiPatientScheduleDel = async (
  id: string,
): Promise<AxiosResponse<any> | void> => {
  const response = await axiosApi().delete<any>(`/schedulePatient/${id}`);
  return response;
};

interface IPSProps {
  scheduleName: string;
  patientId: string;
  startTime: string;
  endTime: string;
}
export const apiPatientScheduleCreate = async (
  data: IPSProps,
): Promise<AxiosResponse<any> | void> => {
  const success = async (): Promise<AxiosResponse<any>> => {
    const response = await axiosApi().post<any>(`/schedulePatient`, data);
    return response;
  };
  return apiWrapper<any>({
    success,
  });
};

interface IPSPropsEdit {
  id: string;
  startTime: string;
  endTime: string;
}
export const apiPatientScheduleEdit = async (
  data: IPSPropsEdit,
): Promise<AxiosResponse<any> | void> => {
  const success = async (): Promise<AxiosResponse<any>> => {
    const response = await axiosApi().patch<any>(`/schedulePatient`, data);
    return response;
  };
  return apiWrapper<any>({
    success,
  });
};
